import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CaretDown, CaretRight, Key, Play, ShieldWarning } from '@phosphor-icons/react';
import API_URL from '../config';

const CONFLICT_LABELS = {
  different_contact_id: 'Account linked to a different HubSpot contact (left alone)',
  resolved_contact_mismatch: 'Email resolves to a different HubSpot contact (PP contact id kept)',
  no_email: 'No email in HubSpot',
  duplicate_email: 'Email shared with another PP contact',
  inactive_account: 'Account exists but is deactivated (no link written)',
  not_a_client_account: 'Email belongs to a non-client account',
};
const LINK_KIND = { set_password: 'Set-password link', set_password_refreshed: 'New set-password link (old one lapsed)', login: 'Login link' };
const LINK_STATUS = { will_write: 'will write', written: 'written', failed: 'failed', blocked: 'blocked — not written' };

const Person = ({ r, extra }) => (
  <li style={{ color: '#101820' }}>
    {r.name || '(no name)'} — {r.email || <em>no email</em>} <span style={{ color: '#A1A1AA' }}>· contact {r.contact_id}</span>
    {extra && <span style={{ color: '#71717A' }}> · {extra}</span>}
  </li>
);

function Group({ id, title, count, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-[#F4F4F5]" data-testid={`pp-access-group-${id}`}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        className="w-full flex items-center gap-2 py-2 text-left text-sm" data-testid={`pp-access-group-${id}-toggle`}>
        {open ? <CaretDown className="w-4 h-4" /> : <CaretRight className="w-4 h-4" />}
        <span className="font-semibold" style={{ color: '#141b4d' }}>{title}</span>
        <span className="ml-auto font-bold tabular-nums" data-testid={`pp-access-count-${id}`}>{count}</span>
      </button>
      {open && <ul className="text-sm space-y-1 pb-3 pl-6 max-h-64 overflow-auto">{children}</ul>}
    </div>
  );
}

function Report({ rep }) {
  const n = rep.counts;
  const byReason = rep.conflicts.reduce((a, c) => ({ ...a, [c.reason]: [...(a[c.reason] || []), c] }), {});
  return (
    <div className="mt-4 border border-[#E4E4E7] px-4 py-2" data-testid={rep.dry_run ? 'pp-access-dryrun-report' : 'pp-access-receipt'}>
      <p className="text-sm font-semibold py-2" style={{ color: '#141b4d' }}>
        {rep.dry_run ? 'Dry run — nothing has changed yet.' : 'Receipt — changes applied.'} {rep.total_contacts} qualifying contact(s).
      </p>
      <Group id="create" title={rep.dry_run ? 'Will create (no email sent)' : 'Created (no email sent)'} count={n.create}>
        {rep.create.map((r) => <Person key={r.contact_id} r={r} />)}
      </Group>
      <Group id="fix" title={rep.dry_run ? 'Will fix the contact id on an existing account' : 'Fixed contact id on existing account'} count={n.fix_contact_id}>
        {rep.fix_contact_id.map((r) => <Person key={r.contact_id} r={r} />)}
      </Group>
      <Group id="match" title="Existing accounts that already match" count={n.already_match}>
        {rep.already_match.map((r) => <Person key={r.contact_id} r={r} />)}
      </Group>
      <Group id="conflicts" title="Conflicts for you to decide" count={n.conflicts}>
        {Object.entries(byReason).map(([reason, rows]) => (
          <li key={reason} className="pt-1">
            <p className="font-semibold" style={{ color: '#B45309' }}>{CONFLICT_LABELS[reason] || reason} ({rows.length})</p>
            <ul className="space-y-1 pl-3">
              {rows.map((r) => <Person key={`${reason}-${r.contact_id}`} r={r}
                extra={r.account_contact_id ? `account has ${r.account_contact_id}` : r.resolved_contact_id ? `resolves to ${r.resolved_contact_id}` : r.other_contact_id ? `also on ${r.other_contact_id}` : null} />)}
            </ul>
          </li>
        ))}
      </Group>
      <Group id="packages" title={rep.dry_run ? 'Held packages that will link' : 'Held packages linked'} count={n.packages_linked}>
        {rep.packages_linked.map((r) => <Person key={r.contact_id} r={r} extra={`${r.count} package(s)`} />)}
      </Group>
      <Group id="links" title={rep.dry_run ? 'HubSpot links that will be written (portal_login_link)' : 'HubSpot links (portal_login_link)'} count={rep.dry_run ? n.links : n.links_written}>
        {rep.links.map((r) => <Person key={r.contact_id} r={r} extra={`${LINK_KIND[r.kind]} · ${LINK_STATUS[r.status]}`} />)}
        {n.links_already_set > 0 && <li style={{ color: '#71717A' }}>{n.links_already_set} already set in HubSpot — skipped.</li>}
      </Group>
      <Group id="merged" title={rep.dry_run ? 'Will link from a merged contact' : 'Linked from a merged contact'} count={n.merged_links ?? 0}>
        {(rep.merged_links || []).map((r) => <Person key={`${r.old_contact_id}-${r.quarter}`} r={r} extra={`${r.quarter}: ${r.old_contact_id} → ${r.new_contact_id}`} />)}
      </Group>
      <Group id="no-package" title="No package yet (nothing created or written)" count={n.no_package ?? 0}>
        {(rep.no_package || []).map((r) => <Person key={r.contact_id} r={r} />)}
      </Group>
      {n.failed > 0 && (
        <Group id="failed" title="Failed" count={n.failed}>
          {rep.failed.map((r) => <Person key={r.contact_id} r={r} extra={r.error} />)}
        </Group>
      )}
    </div>
  );
}

export const PortalAccessPanel = ({ onApplied }) => {
  const [st, setSt] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const timer = useRef(null);

  // `since` = server start time from the POST; keep polling until status catches up to it.
  const poll = async (since = null) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/pp-coverage/portal-access/status`);
      setSt(res.data);
      const stale = since && (!res.data.started_at || new Date(res.data.started_at) < new Date(since));
      if (res.data.running || stale) timer.current = setTimeout(() => poll(since), 1500);
      else if (res.data.result && !res.data.result.dry_run) onApplied?.();
    } catch (e) { toast.error('Could not read job status'); }
  };
  useEffect(() => { poll(); return () => clearTimeout(timer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (dryRun) => {
    setConfirming(false);
    try {
      const res = await axios.post(`${API_URL}/api/admin/pp-coverage/portal-access`, { dry_run: dryRun });
      clearTimeout(timer.current);
      poll(res.data.started_at || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start'); }
  };

  const running = st?.running;
  const rep = st?.result;
  const pct = st?.total ? Math.round((st.done / st.total) * 100) : 0;
  return (
    <div className="card-swiss" data-testid="pp-access-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="max-w-2xl">
          <p className="font-bold" style={{ color: '#141b4d' }}>Set up portal access for Protection Plan clients</p>
          <p className="text-sm mt-1" style={{ color: '#52525B' }}>
            Creates missing accounts quietly, fixes contact ids, links held monitoring packages, and writes each client's
            link to the HubSpot <code>portal_login_link</code> property. The portal sends no emails. Safe to run twice.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => start(true)} disabled={running} data-testid="pp-access-dryrun-btn"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold disabled:opacity-60" style={{ background: '#141b4d', color: '#fff' }}>
            <Key weight="bold" className="w-4 h-4" /> Dry run
          </button>
          {rep?.dry_run && !running && !confirming && (
            <button onClick={() => setConfirming(true)} data-testid="pp-access-apply-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold" style={{ background: '#16A34A', color: '#fff' }}>
              <Play weight="bold" className="w-4 h-4" /> Apply
            </button>
          )}
          {confirming && !running && (
            <div className="flex items-center gap-2" data-testid="pp-access-confirm">
              <span className="text-sm" style={{ color: '#52525B' }}>Create accounts and write portal_login_link? No emails are sent.</span>
              <button onClick={() => start(false)} data-testid="pp-access-confirm-btn"
                className="px-3 py-2 text-sm font-bold" style={{ background: '#16A34A', color: '#fff' }}>Yes, apply</button>
              <button onClick={() => setConfirming(false)} data-testid="pp-access-cancel-btn"
                className="px-3 py-2 text-sm font-semibold border border-[#E4E4E7]">Cancel</button>
            </div>
          )}
        </div>
      </div>
      {running && (
        <div className="mt-4" data-testid="pp-access-progress">
          <p className="text-xs mb-1" style={{ color: '#71717A' }}>{st.dry_run ? 'Dry run' : 'Applying'} — {st.done}/{st.total || '…'} contacts</p>
          <div className="h-2 bg-[#F4F4F5]"><div className="h-2 transition-[width] duration-300" style={{ width: `${pct}%`, background: '#ffd100' }} /></div>
        </div>
      )}
      {st?.error && !running && <p className="mt-3 text-sm text-red-700" data-testid="pp-access-error">Job failed: {st.error}</p>}
      {rep && !running && !rep.hubspot_write_ok && (
        <p className="mt-4 flex items-center gap-2 text-sm font-semibold px-3 py-2" style={{ background: '#FEF3C7', color: '#92400E' }} data-testid="pp-access-scope-warning">
          <ShieldWarning weight="fill" className="w-4 h-4" />
          {rep.hubspot_write_block === 'not_public_url'
            ? "HubSpot links are only written from the live portal (this isn't it). No HubSpot links were written."
            : "HubSpot can't write contacts — add crm.objects.contacts.write to the Private App. No HubSpot links were written."}
        </p>
      )}
      {rep && !running && <Report rep={rep} />}
    </div>
  );
};
